import {
  getApiErrorCode,
  getApiErrorStatus,
  isServerErrorStatus
} from './apiError';
import { dateRequiredMessage, futureDateMessage } from './evidenceHistory';

// Which control gets the message. The evidence controls that render their own errors pass
// `skipErrorToast`, so this is the single place deciding whether a failure belongs to the date
// field or to the form as a whole.
export type EvidenceErrorField = 'date' | 'form';

// Only text that actually came from the API. Deliberately NOT getApiErrorMessage: that falls back
// to `Error.message`, which for a network failure or a local throw is developer text
// ("Network Error", "Upload did not return a document id") and must never reach the user.
const serverMessage = (error: unknown): string | undefined => {
  const status = getApiErrorStatus(error);
  // No status means it never crossed the wire; a 5xx body is an internal string, not user copy.
  if (status === undefined || isServerErrorStatus(status)) return undefined;

  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;

  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message) && message.length) return message.join(', ');

  return undefined;
};

/**
 * Routes a submitRequirementEvidence failure to a field and a message.
 *
 * Branches on CODE, never on message (see utils/apiError.ts) - the two date codes exist precisely
 * so the message can land on the date input instead of the form.
 *
 * COPY PRECEDENCE DIFFERS PER CODE, deliberately. Do not "simplify" this into one rule:
 *
 *   IN_FUTURE  -> OUR copy wins and the server's text is ignored. The server always sends a body
 *                 here and it is the generic "The date cannot be in the future.", which would
 *                 override the pack label forever. Building it here also guarantees that a
 *                 control's client-side guard and this server path emit the IDENTICAL string, so
 *                 the user cannot tell which clock rejected them.
 *   REQUIRED   -> the SERVER's copy wins ("This requirement needs the date the document was
 *                 produced."), which is more specific than anything we would hardcode. No client
 *                 guard exists for this case, so there is no consistency to preserve.
 *   otherwise  -> relay 4xx server copy, else the caller's fallback.
 *
 * REQUIREMENT_STEP_ALREADY_SATISFIED is intentionally absent: it is control flow, not a field
 * error, and the document modal intercepts it before calling this. ACTION_REQUIRES_EVIDENCE is
 * also absent - it belongs to the action-completion PATCH, not this endpoint.
 */
export const evidenceSubmitError = (
  error: unknown,
  { dateLabel = 'Date', fallback }: { dateLabel?: string; fallback: string }
): { field: EvidenceErrorField; message: string } => {
  switch (getApiErrorCode(error)) {
    case 'EVIDENCE_ATTESTATION_DATE_IN_FUTURE':
      return { field: 'date', message: futureDateMessage(dateLabel) };
    case 'EVIDENCE_ATTESTATION_DATE_REQUIRED':
      return {
        field: 'date',
        message: serverMessage(error) ?? dateRequiredMessage(dateLabel)
      };
    default:
      return { field: 'form', message: serverMessage(error) ?? fallback };
  }
};
